import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  Building2, 
  Users, 
  AlertTriangle, 
  CheckSquare,
  Eye,
  FileIcon,
  RefreshCw,
  ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch with httpOnly cookies
const authFetch = async (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include'
  });
};

export default function PGR() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [pgrData, setPgrData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchPGRData();
    }
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchPGRData();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, [authLoading, user]);

  const fetchPGRData = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/pgr/data`);
      if (response.ok) {
        const data = await response.json();
        setPgrData(data);
      }
    } catch (error) {
      console.error('Error fetching PGR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportPGR = async (format) => {
    setExporting(format);
    try {
      const response = await authFetch(`${API}/api/pgr/export/${format}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `PGR_${new Date().toISOString().split('T')[0]}.${format}`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1];
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`PGR exportado em ${format.toUpperCase()} com sucesso!`);
      } else {
        const error = await response.json();
        toast.error(`Erro: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error exporting PGR:', error);
      toast.error('Erro ao exportar PGR');
    } finally {
      setExporting(null);
    }
  };

  const getRiskLevelColor = (nivel) => {
    const colors = {
      'baixo': 'bg-green-100 text-green-800',
      'medio': 'bg-yellow-100 text-yellow-800',
      'médio': 'bg-yellow-100 text-yellow-800',
      'alto': 'bg-orange-100 text-orange-800',
      'critico': 'bg-red-100 text-red-800',
      'crítico': 'bg-red-100 text-red-800'
    };
    return colors[nivel?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pgr-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-7 w-7 text-blue-600" />
            Gerador de PGR
          </h1>
          <p className="text-gray-500">Programa de Gerenciamento de Riscos conforme NR-1</p>
        </div>
        <Button variant="outline" onClick={fetchPGRData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar Dados
        </Button>
      </div>

      {/* Legal Disclaimer Banner */}
      <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 shadow-sm" data-testid="legal-disclaimer">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-800 mb-1">AVISO LEGAL</h3>
            <p className="text-sm text-amber-700 leading-relaxed">
              Esta plataforma fornece ferramentas de auxílio na gestão de SST. A <strong>Ogunhê Desenvolvimento de Sistemas Ltda</strong> (CNPJ: 66.298.458/0001-87) não 
              se responsabiliza pelas informações técnicas contidas neste PGR. É responsabilidade obrigatória do profissional de SST 
              legalmente habilitado revisar e editar todo o conteúdo para garantir a adequação às normas do Ministério do Trabalho e 
              às particularidades do ramo da empresa.
            </p>
          </div>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-2 border-red-100 hover:border-red-300 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <FileIcon className="h-6 w-6 text-red-600" />
              </div>
              Exportar PDF
            </CardTitle>
            <CardDescription>
              Documento formatado para impressão e arquivo oficial
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => exportPGR('pdf')} 
              disabled={exporting === 'pdf'}
              className="w-full bg-red-600 hover:bg-red-700"
              data-testid="export-pdf-btn"
            >
              {exporting === 'pdf' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Baixar PGR em PDF
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              Exportar Word
            </CardTitle>
            <CardDescription>
              Documento editável para personalização adicional
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => exportPGR('word')} 
              disabled={exporting === 'word'}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="export-word-btn"
            >
              {exporting === 'word' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando Word...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Baixar PGR em Word
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Data Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Dados do PGR
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Ocultar Detalhes' : 'Ver Detalhes'}
            </Button>
          </div>
          <CardDescription>
            Informações que serão incluídas no documento
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <Building2 className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{pgrData?.company?.nome?.substring(0, 15) || 'N/A'}</p>
              <p className="text-sm text-gray-500">Empresa</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-blue-600">{pgrData?.total_employees || 0}</p>
              <p className="text-sm text-gray-500">Funcionários</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <AlertTriangle className="h-8 w-8 mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold text-orange-600">{pgrData?.risks?.length || 0}</p>
              <p className="text-sm text-gray-500">Riscos</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <CheckSquare className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{pgrData?.action_plans?.length || 0}</p>
              <p className="text-sm text-gray-500">Planos de Ação</p>
            </div>
          </div>

          {/* Detailed Preview */}
          {showPreview && (
            <div className="space-y-6 border-t pt-6">
              {/* Company Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Identificação da Empresa</h4>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Razão Social</p>
                    <p className="font-medium">{pgrData?.company?.nome || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">CNPJ</p>
                    <p className="font-medium">{pgrData?.company?.cnpj || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Setor de Atividade</p>
                    <p className="font-medium">{pgrData?.company?.setor || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total de Colaboradores</p>
                    <p className="font-medium">{pgrData?.total_employees || 0}</p>
                  </div>
                </div>
              </div>

              {/* Employees by Sector */}
              {pgrData?.employees_by_sector && Object.keys(pgrData.employees_by_sector).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Colaboradores por Setor</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(pgrData.employees_by_sector).map(([sector, count]) => (
                      <Badge key={sector} variant="outline" className="px-3 py-1">
                        {sector}: <span className="font-bold ml-1">{count}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks */}
              {pgrData?.risks?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Inventário de Riscos ({pgrData.risks.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Tipo</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Descrição</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Nível</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Setor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pgrData.risks.slice(0, 10).map((risk) => (
                          <tr key={risk.id || `${risk.tipo}-${risk.setor}`} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3">{risk.tipo}</td>
                            <td className="py-2 px-3 max-w-xs truncate">{risk.descricao}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(risk.nivel)}`}>
                                {risk.nivel}
                              </span>
                            </td>
                            <td className="py-2 px-3">{risk.setor || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {pgrData.risks.length > 10 && (
                      <p className="text-sm text-gray-500 mt-2 text-center">
                        ... e mais {pgrData.risks.length - 10} riscos
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Action Plans */}
              {pgrData?.action_plans?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Planos de Ação ({pgrData.action_plans.length})</h4>
                  <div className="space-y-2">
                    {pgrData.action_plans.slice(0, 5).map((plan) => (
                      <div key={plan.id || plan.titulo} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{plan.titulo}</p>
                          <p className="text-sm text-gray-500">Responsável: {plan.responsavel || 'N/A'}</p>
                        </div>
                        <Badge variant={plan.status === 'concluido' ? 'success' : 'secondary'}>
                          {plan.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900">Sobre o PGR</p>
              <p className="text-sm text-blue-700 mt-1">
                O Programa de Gerenciamento de Riscos (PGR) é um documento obrigatório conforme a NR-1 
                que deve conter, no mínimo, o inventário de riscos ocupacionais e o plano de ação. 
                Este documento é gerado automaticamente com base nos dados cadastrados no sistema.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
